import { useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import { ProjectWithAssigneesDto } from '../../../commonTypes/acs';
import { useHttpGetProjectSummaryOverview, useHttpGetUserTimeLogs } from '../../../hooks';
import { Table, TableRow, TableHeadCell, TableCell, MixerLoader, Pagination } from '../../Common';
import CalendarMonthShifter from '../../CalendarMonthShifter';
import { getCurrencySymbol } from '../../../util/general';

interface ProjectTimesheetTabProps {
    project: ProjectWithAssigneesDto;
    showOnlyOwnData?: boolean;
    currentUserSub?: string;
    showRates?: boolean;
    currentUserName?: string;
    currentUserRole?: string;
    currentUserImageUrl?: string;
    currentUserRate?: number | null;
}

interface TimesheetRow {
    userSub: string;
    member: string;
    role: string;
    hourlyRate?: number | null;
    totalUnsubmitted: string;
    totalSubmitted: string;
    imageUrl?: string;
}

type SortColumn = 'MEMBER' | 'ROLE' | 'HOURLY_RATE' | 'UNSUBMITTED' | 'SUBMITTED';

const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const ProjectTimesheetTab = ({
    project,
    showOnlyOwnData = false,
    currentUserSub,
    showRates = false,
    currentUserName,
    currentUserRole,
    currentUserImageUrl,
    currentUserRate,
}: ProjectTimesheetTabProps) => {
    const [currentMonth, setCurrentMonth] = useState<DateTime>(DateTime.now());
    const [sortBy, setSortBy] = useState<SortColumn>('MEMBER');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const month = currentMonth.toFormat('MMM');
    const year = currentMonth.year;

    const { isReady: isProjectSummaryReady, data } = useHttpGetProjectSummaryOverview(
        showOnlyOwnData ? '' : project.id,
        showOnlyOwnData ? '' : month,
        showOnlyOwnData ? 0 : year
    );

    const {
        totalSubmitted,
        totalUnsubmitted,
        isReady: isTimeLogsReady,
    } = useHttpGetUserTimeLogs(showOnlyOwnData ? { month, year, projectId: project.id } : { month: '', year: 0, projectId: '' });

    const isReady = showOnlyOwnData ? isTimeLogsReady : isProjectSummaryReady;

    const timesheetData = useMemo<TimesheetRow[]>(() => {
        if (showOnlyOwnData) {
            return [
                {
                    userSub: currentUserSub || '',
                    member: currentUserName || 'Unknown',
                    role: currentUserRole || '',
                    hourlyRate: currentUserRate,
                    totalUnsubmitted: formatTime(totalUnsubmitted * 3600),
                    totalSubmitted: formatTime(totalSubmitted * 3600),
                    imageUrl: currentUserImageUrl,
                },
            ];
        }

        if (!data) return [];

        return data.map(item => {
            const assignee = project.projectAssignees?.find(a => a.userSub === item.userSub);
            const rate = assignee?.rate ?? null;
            const title = assignee?.title ?? '';

            return {
                userSub: item.userSub,
                member: item.member,
                role: title,
                hourlyRate: rate,
                totalUnsubmitted: formatTime(item.unsubmitted),
                totalSubmitted: formatTime(item.submitted),
                imageUrl: item.imageUrl,
            };
        });
    }, [data, project.projectAssignees, showOnlyOwnData, currentUserSub, totalSubmitted, totalUnsubmitted]);

    const sortedData = useMemo(() => {
        const result = [...timesheetData];

        result.sort((a, b) => {
            const dir = sortDirection === 'ASC' ? 1 : -1;

            switch (sortBy) {
                case 'MEMBER':
                    return dir * a.member.localeCompare(b.member);
                case 'ROLE':
                    return dir * a.role.localeCompare(b.role);
                case 'HOURLY_RATE':
                    return dir * ((a.hourlyRate ?? 0) - (b.hourlyRate ?? 0));
                case 'UNSUBMITTED':
                    return dir * a.totalUnsubmitted.localeCompare(b.totalUnsubmitted);
                case 'SUBMITTED':
                    return dir * a.totalSubmitted.localeCompare(b.totalSubmitted);
                default:
                    return 0;
            }
        });

        return result;
    }, [timesheetData, sortBy, sortDirection]);

    const paginatedData = useMemo(() => {
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, itemsPerPage]);

    const handleSort = (column: SortColumn) => {
        setSortDirection(prev => (column === sortBy ? (prev === 'ASC' ? 'DESC' : 'ASC') : 'ASC'));
        setSortBy(column);
        setCurrentPage(0);
    };

    const getArrowClass = (column: SortColumn) => {
        if (sortBy !== column) return 'invoices__header-cell--sortable__arrow';
        return `invoices__header-cell--sortable__arrow invoices__header-cell--sortable__arrow--${sortDirection.toLowerCase()}`;
    };

    const handlePreviousMonth = () => {
        setCurrentMonth(prev => prev.minus({ month: 1 }));
    };

    const handleNextMonth = () => {
        setCurrentMonth(prev => prev.plus({ month: 1 }));
    };

    if (!isReady) {
        if (!isReady) return <MixerLoader position="centerOnParent" />;
    }

    return (
        <div className="project-timesheet-tab">
            <div className="project-timesheet-tab__header">
                <h2 className="project-timesheet-tab__title">Total Logged Time</h2>
                <CalendarMonthShifter
                    formatOptions={{ month: 'long', year: 'numeric' }}
                    currentDate={currentMonth}
                    previous={handlePreviousMonth}
                    next={handleNextMonth}
                />
            </div>
            <div className="projects-table__wrapper">
                <Table className="invoices__table">
                    <TableRow className="invoices__header-cell--background">
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Member
                                <img
                                    src={sortBy === 'MEMBER' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('MEMBER')}
                                    onClick={() => handleSort('MEMBER')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Role
                                <img
                                    src={sortBy === 'ROLE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('ROLE')}
                                    onClick={() => handleSort('ROLE')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        {showRates && (
                            <TableHeadCell className="invoices__header-cell--background">
                                <div className="invoices__header-cell invoices__header-cell--sortable">
                                    Rate
                                    <img
                                        src={sortBy === 'HOURLY_RATE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                        className={getArrowClass('HOURLY_RATE')}
                                        onClick={() => handleSort('HOURLY_RATE')}
                                        alt="Sort"
                                    />
                                </div>
                            </TableHeadCell>
                        )}
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Total Unsubmitted
                                <img
                                    src={sortBy === 'UNSUBMITTED' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('UNSUBMITTED')}
                                    onClick={() => handleSort('UNSUBMITTED')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Total Submitted
                                <img
                                    src={sortBy === 'SUBMITTED' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('SUBMITTED')}
                                    onClick={() => handleSort('SUBMITTED')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                    </TableRow>

                    {paginatedData.map((row, index) => (
                        <TableRow key={row.userSub} className={`invoices__row ${index % 2 === 0 ? 'invoices__row--white' : 'invoices__row--gray'}`}>
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <div className="project-timesheet-tab__member">
                                        <img
                                            className="project-timesheet-tab__avatar"
                                            src={row.imageUrl || '/svg/person.svg'}
                                            alt={row.member}
                                            onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                                        />
                                        <span className="invoices__cell-text">{row.member}</span>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <span className="invoices__cell-text">{row.role}</span>
                                </div>
                            </TableCell>
                            {showRates && (
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">
                                            {row.hourlyRate !== null ? `${getCurrencySymbol(project.currency)}${row.hourlyRate?.toFixed(2)}` : '-'}
                                        </span>
                                    </div>
                                </TableCell>
                            )}
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <span className="invoices__cell-text">{row.totalUnsubmitted}</span>
                                </div>
                            </TableCell>
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <span className="invoices__cell-text">{row.totalSubmitted}</span>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                    {sortedData.length > 10 && (
                        <TableRow className="projects-table__pagination-row">
                            <TableCell colSpan={showRates ? 5 : 4} className="projects-table__pagination-cell">
                                <Pagination
                                    total={sortedData.length}
                                    elementsPerPage={itemsPerPage}
                                    setElementsPerPage={setItemsPerPage}
                                    page={currentPage}
                                    setPage={setCurrentPage}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </Table>
            </div>
        </div>
    );
};
